import { PLAN_TYPES } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { trackFirstRequest } from '@common/metrics/pageLoad';
import { HUB_URL } from '@common/packageHub/constants';
import { useReplayModeType } from '@common/Replay/utils';
import { Private_Routes, VAPP_Private_Routes } from '@common/url';
import { UserProfileDropdown } from '@common/UserProfileDropdown';
import { useIsProfessionalVerticalApp } from '@common/VerticalApps/utils';
import {
  SearchMdIcon,
  FolderOpenIcon,
  ActivityIcon,
  BuildingBIcon,
  HelpCircleIcon,
  DownloadBIcon,
  DownloadDIcon,
  LogoProphecyNameIcon,
  HomeCIcon,
  DatabaseCIcon
} from '@prophecy/icons';
import { ICON_CLASS } from '@prophecy/icons/tokens';
import { negateVariable, resolveCssVariable, theme } from '@prophecy/theme';
import { Stack, Popover, Button, Input, Ellipsis, toast, Dialog } from '@prophecy/ui-v3';
import { Avatar } from '@prophecy/ui-v3/Avatar';
import { StyledInputContainer } from '@prophecy/ui-v3/Input/styled';
import { hideScrollbarCss } from '@prophecy/ui-v3/Misc/HideScrollbar';
import { Text } from '@prophecy/ui-v3/Typography';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEffect, useState } from 'react';
import { useLocation, matchPath, Link } from 'react-router-dom';
import { css, styled } from 'styled-components';

import { useEnabledTranspilersAndLanguages } from '../context/enabledTranspilers';
import { useGlobalVariables } from '../context/globalConfig/securedConfigHook';
import { useUser } from '../context/user';
import { AiChatButton } from '../InKeepAI';
import { downloadWebSocketLogsViaWorker } from '../logger/websocketMessageLogger';
import { isWebSocketLoggingEnabled } from '../utils/localstorage-keys';
import {
  NavItemStyleProps,
  NavTooltip,
  navItemCSS,
  StyledLogo,
  showAiAssistant,
  SupportPopover,
  VerticalAppSideNavProjectsSection
} from './components';
import { useHandleNavItemClick, useHoverExpandSideNavBar } from './hooks';

const COLLAPSED_WIDTH = 56;
const OVERLAY_WIDTH = 360;

const verticalNavItemContentCSS = css<NavItemStyleProps & { $collapsed?: boolean }>`
  color: ${(p) => (p.$active ? theme.colors.white : theme.foregroundColor.quinary)};

  .${ICON_CLASS} {
    font-size: ${theme.sizes.x20};
  }

  &:hover:not([disabled]) {
    color: ${theme.colors.white};
  }

  .vertical-nav-item-label {
    color: ${(p) => (p.$active ? theme.colors.gray25 : theme.foregroundColor.senary)};
  }

  &:hover:not([disabled]) .vertical-nav-item-label {
    color: ${(p) => (p.$active ? theme.colors.gray25 : theme.colors.white)};
  }
`;

const verticalNavExpandedItemCSS = css<{ $collapsed: boolean }>`
  ${(p) =>
    !p.$collapsed &&
    css`
      &&& {
        width: 100%;
        max-width: 100%;
        height: ${theme.sizes.x40};
        justify-content: flex-start;
        gap: ${theme.spaces.x12};
        padding: ${theme.spaces.x8};
      }
    `}
`;

const StyledLogoName = styled(LogoProphecyNameIcon)<{ $visible: boolean }>`
  align-self: flex-start;
  margin-top: ${theme.spaces.x2};
  height: ${theme.sizes.x32};
  width: auto;
  color: ${theme.colors.white};
  flex-shrink: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 150ms ease;
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
`;

const UserProfileContainer = styled(Button)<{ $collapsed: boolean }>`
  &&& {
    box-sizing: border-box;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: ${(p) => (p.$collapsed ? 'center' : 'flex-start')};
    gap: ${theme.spaces.x8};
    width: ${(p) => (p.$collapsed ? 'auto' : '100%')};
    padding: ${theme.spaces.x12};
    background-color: ${(p) => (p.$collapsed ? 'transparent' : theme.colors.gray800)};
    border-radius: ${theme.radius.x8};
    border: ${(p) => (p.$collapsed ? 'none' : `1px solid ${theme.colors.gray700}`)};
    height: calc(${theme.sizes.x32} + 2 * ${theme.spaces.x12});
    overflow: hidden;

    &:hover {
      background-color: ${theme.colors.gray700};
    }
  }
`;
const StyledVerticalSideNavbarShell = styled.div`
  position: relative;
  z-index: ${theme.zLayer.xxxl};
  flex-shrink: 0;
  width: ${COLLAPSED_WIDTH}px;
  background-color: ${theme.colors.gray900};
`;

const StyledVerticalSideNavbar = styled(Stack)<{ $overlayActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $overlayActive }) => ($overlayActive ? OVERLAY_WIDTH : COLLAPSED_WIDTH)}px;
  pointer-events: all;
  background-color: ${theme.colors.gray900};
  padding: ${theme.spaces.x16};
  font-family: ${theme.fontFamily.text};
  overflow: auto;
  ${hideScrollbarCss};

  box-shadow: ${({ $overlayActive }) => ($overlayActive ? theme.shadows.xl : 'none')};
  transition:
    width 200ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 200ms ease;
`;

const SidebarLogoWrapper = styled(Stack)<{ $collapsed: boolean }>`
  width: calc(100% + ${theme.spaces.x32});
  margin-left: ${negateVariable(theme.spaces.x16)};
  margin-right: ${negateVariable(theme.spaces.x16)};
  padding-left: ${(p) => (p.$collapsed ? theme.spaces.x8 : theme.spaces.x16)};
  padding-right: ${theme.spaces.x8};
  padding-bottom: ${theme.spaces.x16};
`;

const StyledLogoContainer = styled(Stack)`
  min-width: 40px;
  height: 40px;
`;

const StyledLogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spaces.x8};
`;

function VerticalNavItem({
  to,
  title,
  children,
  onClick,
  activeOnRoutes = [`${to}/*`],
  dataTestId,
  collapsed,
  disabled,
  message
}: {
  to: string;
  title: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  activeOnRoutes?: string[];
  dataTestId: string;
  collapsed: boolean;
  disabled?: boolean;
  message?: string;
}) {
  const location = useLocation();
  const routeMatched = activeOnRoutes.some((pattern) => Boolean(matchPath(pattern, location.pathname)));
  const { handleNavItemClick } = useHandleNavItemClick();

  const handleOnClick = usePersistentCallback(async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (disabled) {
      return await Dialog.warning({
        'data-test-id': 'disabled-vertical-nav-warning-dialog',
        subTitle: message,
        children: null,
        closeButton: null
      });
    }
    return handleNavItemClick(event, to, onClick);
  });
  const link = (
    <StyledVerticalNavLink
      as={Link}
      to={to}
      onClick={handleOnClick}
      $active={routeMatched}
      $collapsed={collapsed}
      aria-label={`Go to ${title}`}
      data-test-id={dataTestId}>
      {children}
      {!collapsed && (
        <Text className='vertical-nav-item-label' level='sm'>
          {title}
        </Text>
      )}
    </StyledVerticalNavLink>
  );

  return collapsed ? <NavTooltip title={title}>{link}</NavTooltip> : link;
}

const verticalNavItemLabelCss = css`
  .vertical-nav-item-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: opacity 150ms ease;
  }
`;

const StyledVerticalNavLink = styled(Button)<NavItemStyleProps & { $collapsed: boolean }>`
  ${navItemCSS}

  &&& {
    box-sizing: border-box;
  }

  ${verticalNavItemContentCSS}
  ${verticalNavExpandedItemCSS}

  ${verticalNavItemLabelCss}

  ${(p) =>
    p.$collapsed &&
    css`
      .vertical-nav-item-label {
        opacity: 0;
      }
    `}
`;

const StyledVerticalNavButton = styled.button<NavItemStyleProps & { $collapsed: boolean }>`
  all: unset;
  ${navItemCSS}

  &&& {
    box-sizing: border-box;
  }

  ${verticalNavItemLabelCss}

  ${verticalNavItemContentCSS}
  ${verticalNavExpandedItemCSS}
`;

const SearchBar = styled(Input)`
  width: 100%;

  ${StyledInputContainer} {
    height: ${theme.sizes.x40};
    background-color: ${theme.colors.gray800};
    border: 1px solid ${theme.colors.gray700};
  }

  input {
    color: ${theme.colors.white};
  }
`;

const SearchBarShortcut = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radius.x6};
  border: 1px solid ${theme.colors.gray700};
  padding: 2px ${theme.spaces.x6};
  font-size: ${theme.fontSizes.x12};
  color: ${theme.colors.gray400};
  line-height: 1;
  background: ${theme.colors.gray800};
  white-space: nowrap;
`;

export const VerticalAppSideNavBar = () => {
  const [isSupportPopoverOpen, toggleSupportPopover] = useState(false);
  const [aiPopoverOpen, setAiPopoverOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    containerRef,
    visuallyCollapsed,
    overlayActive,
    handlePointerEnter,
    handlePointerLeave,
    handleLockedOpenChange,
    handleInteractOutside,
    handleFocusCapture,
    handleBlurCapture,
    collapseSideNav
  } = useHoverExpandSideNavBar();
  const { hasReplayShareMode } = useReplayModeType();
  const { handleNavItemClick } = useHandleNavItemClick();

  const {
    meteringInfo: { plan }
  } = useProfessionalEditionCreditsContext();

  const isFabricSet = useGlobalVariables().getFabricStatus;
  // Alteryx imports are a Professional-edition Pro feature; Structured Finance Pro doesn't include them
  const isProfessionalVerticalApp = useIsProfessionalVerticalApp();
  // #13243 makes the enabled-transpilers API the single source of truth for transpiler availability.
  const { supportedTranspilerType } = useEnabledTranspilersAndLanguages();
  const isTranspilerEnabled = Boolean(supportedTranspilerType?.size);

  // Also drop the hover-lock; Radix's onVisibleChange doesn't fire for programmatic closes.
  const closeSupportPopover = () => {
    toggleSupportPopover(false);
    handleLockedOpenChange(false);
    collapseSideNav();
  };

  const openAiPopover = () => {
    setAiPopoverOpen(true);
    closeSupportPopover();
  };

  const user = useUser();
  const userName = `${user.user?.firstName} ${user.user?.lastName}`;

  // Page-load milestone: navigation-timing first-request measurement (fires once per page load).
  useEffect(() => {
    trackFirstRequest();
  }, []);
  const isShowingSearchResults = !visuallyCollapsed && searchQuery.trim().length > 0;

  return (
    <>
      <StyledVerticalSideNavbarShell
        ref={containerRef}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
        data-no-autofocus>
        <StyledVerticalSideNavbar
          align='space-between'
          direction='vertical'
          width='100%'
          height='100%'
          $overlayActive={overlayActive}>
          <Stack gap={theme.spaces.x4} alignY={visuallyCollapsed ? 'center' : 'start'} width='100%'>
            <SidebarLogoWrapper
              $collapsed={visuallyCollapsed}
              direction='horizontal'
              alignY='center'
              align='space-between'
              gap={theme.spaces.x10}
              width='100%'>
              <StyledLogoLink
                to={VAPP_Private_Routes.Home.getUrl()}
                onClick={(e) => handleNavItemClick(e, VAPP_Private_Routes.Home.getUrl())}
                aria-label='Go to home'
                data-test-id='vertical-nav-logo-link'>
                <StyledLogoContainer alignY='center' align='start'>
                  <StyledLogo />
                </StyledLogoContainer>
                <StyledLogoName
                  height={resolveCssVariable(theme.sizes.x32)}
                  width={resolveCssVariable(theme.spaces.x96)}
                  $visible={!visuallyCollapsed}
                />
              </StyledLogoLink>
            </SidebarLogoWrapper>

            {!hasReplayShareMode &&
              (visuallyCollapsed ? (
                <NavTooltip title='Search'>
                  <StyledVerticalNavLink
                    aria-label='Search'
                    data-test-id='vertical-nav-search-button'
                    variant='plain'
                    icon={<SearchMdIcon type='default' size='m' />}
                    $collapsed={visuallyCollapsed}></StyledVerticalNavLink>
                </NavTooltip>
              ) : (
                <SearchBar
                  data-test-id='vertical-nav-search-input'
                  placeholder='Search'
                  value={searchQuery}
                  onChange={(value) => setSearchQuery(value)}
                  inputSize='s'
                  iconPlacement='left'
                  icon={<SearchMdIcon type='default' size='s' />}
                  addOn={<SearchBarShortcut>⌘K</SearchBarShortcut>}
                  addOnPlacement='right'
                  addOnSeparator={false}
                />
              ))}

            <Stack
              gap={theme.spaces.x4}
              alignY={visuallyCollapsed ? 'center' : 'start'}
              width='100%'
              padding={`${theme.spaces.x8} 0 0 0`}>
              {!isShowingSearchResults && (
                <>
                  <VerticalNavItem
                    to={VAPP_Private_Routes.Home.getUrl()}
                    title='Home'
                    dataTestId='vertical-nav-home-button'
                    collapsed={visuallyCollapsed}>
                    <HomeCIcon type='default' size='s' />
                  </VerticalNavItem>
                  <VerticalNavItem
                    to={VAPP_Private_Routes.Databox.getUrl()}
                    title='Databox'
                    dataTestId='vertical-nav-databox-button'
                    activeOnRoutes={[VAPP_Private_Routes.Databox.url]}
                    collapsed={visuallyCollapsed}>
                    <DatabaseCIcon type='default' size='s' />
                  </VerticalNavItem>
                  <VerticalNavItem
                    to={VAPP_Private_Routes.Monitoring.getUrl(undefined)}
                    title='Monitoring'
                    dataTestId='vertical-nav-monitoring-button'
                    collapsed={visuallyCollapsed}>
                    <ActivityIcon type='default' size='s' />
                  </VerticalNavItem>
                  {isProfessionalVerticalApp && plan === PLAN_TYPES.Pro && isTranspilerEnabled && (
                    <VerticalNavItem
                      to={Private_Routes.Transpiler.getUrl()}
                      disabled={!isFabricSet}
                      message='Import is disabled. Connect to a fabric to enable imports.'
                      title='Import'
                      dataTestId='vertical-nav-import-button'
                      activeOnRoutes={[`${Private_Routes.Transpiler.url}/*`]}
                      collapsed={visuallyCollapsed}>
                      <DownloadDIcon type='default' size='s' />
                    </VerticalNavItem>
                  )}
                  <VerticalNavItem
                    to={VAPP_Private_Routes.Project_Listing.getUrl()}
                    title='Projects'
                    dataTestId='vertical-nav-projects-button'
                    activeOnRoutes={[VAPP_Private_Routes.Project_Listing.url]}
                    collapsed={visuallyCollapsed}>
                    <FolderOpenIcon type='extended' size='s' />
                  </VerticalNavItem>
                </>
              )}
              {!visuallyCollapsed && !hasReplayShareMode && (
                <VerticalAppSideNavProjectsSection searchQuery={searchQuery} />
              )}
            </Stack>
          </Stack>

          <Stack gap={theme.spaces.x4} alignY={visuallyCollapsed ? 'center' : 'start'} width='100%'>
            <Stack
              gap={theme.spaces.x4}
              alignY={visuallyCollapsed ? 'center' : 'start'}
              width='100%'
              padding={`${theme.spaces.x16} 0`}>
              {isWebSocketLoggingEnabled() && (
                <StyledVerticalNavButton
                  $collapsed={visuallyCollapsed}
                  onClick={async () => {
                    const hasLogs = await downloadWebSocketLogsViaWorker();
                    if (!hasLogs) {
                      toast.info({
                        content: 'No WebSocket logs to download.',
                        'data-test-id': 'websocket-logs-empty-toast'
                      });
                    }
                  }}
                  aria-label='Download WebSocket logs'
                  data-test-id='side-nav-download-websocket-logs-button'>
                  <Stack
                    direction='horizontal'
                    alignY='center'
                    gap={theme.spaces.x12}
                    width={visuallyCollapsed ? 'auto' : '100%'}>
                    <DownloadBIcon type='default' size='m' />
                    {!visuallyCollapsed && (
                      <Text className='vertical-nav-item-label' level='sm' weight={theme.fontWeight.medium}>
                        Download WebSocket logs
                      </Text>
                    )}
                  </Stack>
                </StyledVerticalNavButton>
              )}

              <VerticalNavItem
                to={HUB_URL.PACKAGE}
                title='Hub'
                dataTestId='vertical-nav-package-hub-button'
                activeOnRoutes={[Private_Routes.PackageHubList.url]}
                collapsed={visuallyCollapsed}>
                <BuildingBIcon type='default' size='s' />
              </VerticalNavItem>

              <Popover
                placement='right'
                overlayClassName='side-nav-more-popover'
                placementOffset={parseInt(resolveCssVariable(theme.spaces.x24), 10)}
                align='end'
                visible={isSupportPopoverOpen}
                onVisibleChange={(open) => {
                  toggleSupportPopover(open);
                  handleLockedOpenChange(open);
                }}
                onInteractOutside={handleInteractOutside}
                overlay={<SupportPopover openAiPopover={openAiPopover} onClose={closeSupportPopover} />}>
                <StyledVerticalNavButton
                  $collapsed={visuallyCollapsed}
                  aria-label='Go to Help and Support'
                  type='button'
                  data-test-id='vertical-nav-help-button'>
                  <Stack
                    direction='horizontal'
                    alignY='center'
                    gap={theme.spaces.x12}
                    width={visuallyCollapsed ? 'auto' : '100%'}>
                    <HelpCircleIcon type='default' size='s' />
                    {!visuallyCollapsed && (
                      <Text className='vertical-nav-item-label' level='sm' weight={theme.fontWeight.medium}>
                        Help
                      </Text>
                    )}
                  </Stack>
                </StyledVerticalNavButton>
              </Popover>
            </Stack>
            {!hasReplayShareMode && (
              <UserProfileDropdown
                placement='top'
                align='start'
                placementOffset={parseInt(resolveCssVariable(theme.spaces.x8), 10)}
                onVisibleChange={handleLockedOpenChange}
                onInteractOutside={handleInteractOutside}
                collapseSideNav={collapseSideNav}>
                <UserProfileContainer
                  $collapsed={visuallyCollapsed}
                  variant='plain'
                  iconPlacement='left'
                  icon={<Avatar size='s' text={userName} />}
                  aria-label='Open user menu'
                  data-test-id='vertical-nav-user-profile-button'>
                  {!visuallyCollapsed && (
                    <Stack direction='vertical'>
                      <Ellipsis tooltip={true} tooltipProps={{ placement: 'right' }}>
                        <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.white}>
                          {userName}
                        </Text>
                      </Ellipsis>
                      <Ellipsis tooltip={true} tooltipProps={{ placement: 'right' }}>
                        <Text level='sm' tone={theme.colors.gray500}>
                          {user.user?.email}
                        </Text>
                      </Ellipsis>
                    </Stack>
                  )}
                </UserProfileContainer>
              </UserProfileDropdown>
            )}
          </Stack>
        </StyledVerticalSideNavbar>
      </StyledVerticalSideNavbarShell>
      {showAiAssistant && <AiChatButton isOpen={aiPopoverOpen} onClose={() => setAiPopoverOpen(false)} />}
    </>
  );
};
